import { ReactNode, useRef, useState } from 'react'
import FileSaver from 'file-saver'
import { useIntl } from 'react-intl'

import {
  mdiCloudDownloadOutline,
  mdiAlertCircleOutline,
  mdiCheck,
} from '@mdi/js'

import { SpinnerCentered } from 'components/Loader'
import { ProgressCircle } from 'components/ProgressCircle'
import {
  DownloadIcon,
  DownloadIconWrapper,
  ErrorIcon,
  StyledDownloadButton,
} from './styles'
import { downloadButtonCellMessages } from './messages'
import { commonMessages } from 'app/i18n/messages'
import theme from 'theme_'

interface DownloadButtonCellProps {
  url: string
  fileName: string
  children?: ReactNode
}

export const DownloadButtonCell = (props: DownloadButtonCellProps) => {
  const { formatMessage } = useIntl()

  const { fileName, url, children } = props
  const xhrRef = useRef<XMLHttpRequest>()
  const [isLoading, setIsLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(false)

  const handleClick = async () => {
    xhrRef.current?.abort()
    xhrRef.current = new XMLHttpRequest()
    new Promise<void>((resolve, reject) => {
      setIsLoading(true)
      setProgress(0)

      const listeners = {
        abort() {
          setShowError(false)
          setShowSuccess(false)
          setIsLoading(false)
          setProgress(0)
          reject()
        },
        error() {
          setShowError(true)
          setShowSuccess(false)
          setIsLoading(false)
          setProgress(0)
          reject()
        },
        load() {
          resolve()
          setShowError(false)
          setShowSuccess(true)
          setIsLoading(false)
          setProgress(0)
        },
        loadend() {
          reject()
        },
        loadstart() {
          setShowError(false)
          setShowSuccess(false)
          setIsLoading(true)
          setProgress(0)
        },
        progress(event: Event) {
          setShowError(false)
          setShowSuccess(false)
          setIsLoading(true)
          const progressEvent = event as ProgressEvent
          if (progressEvent.lengthComputable) {
            setProgress(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            )
          } else {
            setProgress(NaN)
          }
        },
        timeout() {
          setShowError(false)
          setShowSuccess(false)
          setIsLoading(false)
          setProgress(0)
          reject()
        },
      }

      Object.entries(listeners).forEach(([event, callback]) => {
        xhrRef.current!.addEventListener(event, callback)
      })
      xhrRef.current!.responseType = 'blob'
      xhrRef.current!.open('GET', url)
      xhrRef.current!.setRequestHeader('Accept', 'application/octet-stream')
      xhrRef.current!.send()
    }).then(() => {
      FileSaver.saveAs(xhrRef.current!.response, fileName)
    })
  }

  if (!url) return null

  return (
    <StyledDownloadButton
      onClick={handleClick}
      disabled={isLoading || showError}
      title={formatMessage(downloadButtonCellMessages.title)}
    >
      {showError && (
        <ErrorIcon
          path={mdiAlertCircleOutline}
          title={formatMessage(commonMessages.commonError)}
          description={formatMessage(commonMessages.commonError)}
          size={1}
        />
      )}
      {isLoading &&
        (Number.isNaN(progress) ? (
          <SpinnerCentered size="24" />
        ) : (
          <ProgressCircle
            percent={progress}
            size={2}
            color={theme.colors.dimmedGold}
          />
        ))}
      {showSuccess && <DownloadIcon path={mdiCheck} size={1} />}
      {!isLoading && !showSuccess && !showError && (
        <DownloadIconWrapper>
          <DownloadIcon path={mdiCloudDownloadOutline} size={1} />
          {children}
        </DownloadIconWrapper>
      )}
    </StyledDownloadButton>
  )
}
